import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';

import CheckboxFormik from '~/components/CheckboxFormik';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import Grid from '~/components/core/Atomic/Grid/Grid';
import MenuItem from '~/components/core/Atomic/MenuItem';
import { InfoIcon } from '~/components/icons';

import { reportAxiosError } from '../../Utils';
import CardDialog from '../CardDialog';
import { LoadingSwitch } from '../core';
import TooltipIcon from '../core/TooltipIcon';
import useExposureClosingReasons from '../hooks/useExposureClosingReasons';
import TextFieldFormik from '../TextFieldFormik';

import { useStyles } from '../../assets/styles';

function CloseExposureDialog(props) {
  const { claim, exposure, onClose, onUpdate, title } = props;

  const handleCloseExposure = (values) => {
    return axios
      .post(`/api/v1/claims/${claim.id}/exposures/${exposure.id}/close`, {
        reason: values.reason,
        extra: values.extra,
        should_dismiss_all_exposure_notifications: values.should_dismiss_all_exposure_notifications,
      })
      .then(() => {
        onUpdate();
        onClose();
      })
      .catch((error) => {
        reportAxiosError(error);
        throw error;
      });
  };

  return (
    <ClosingReasonDialog
      claim={claim}
      exposures={[exposure]}
      onClose={onClose}
      onReasonChosen={handleCloseExposure}
      title={title}
    />
  );
}

CloseExposureDialog.propTypes = {
  claim: PropTypes.object.isRequired,
  exposure: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  title: PropTypes.string,
};

function ClosingReasonDialog({ title, claim, onReasonChosen, onClose, exposures }) {
  const { classes } = useStyles();

  const { possibleReasons, exposuresClosingReasons, isClosingReasonsLoaded, isNewClosingReason } =
    useExposureClosingReasons(claim, exposures);

  return (
    <Formik
      initialValues={{ reason: '', extra: '', should_dismiss_all_exposure_notifications: false }}
      validationSchema={Yup.object().shape({
        reason: Yup.string().required('Required'),
        extra: Yup.string().when('reason', {
          is: (val) =>
            (!isNewClosingReason && val && exposuresClosingReasons[val].extra_needed) ||
            (isNewClosingReason && val && exposuresClosingReasons[val].is_note_required),
          then: Yup.string().required('Required'),
        }),
      })}
      enableReinitialize
      onSubmit={(values, formikProps) => {
        onReasonChosen(values)
          .then(() => formikProps.resetForm())
          .catch(() => {
            formikProps.setSubmitting(false);
          });
      }}
    >
      {(formikProps) => {
        const { values, isSubmitting, handleReset, handleSubmit } = formikProps;
        const showExtra = values.reason && exposuresClosingReasons[values.reason].extra_needed;
        return (
          <CardDialog
            isDialog={true}
            open={true}
            title={title || 'Close Exposure'}
            fullWidth
            maxWidth="sm"
            onClose={() => {
              handleReset();
              onClose();
            }}
            preventClose={isSubmitting}
          >
            <LoadingSwitch isLoading={!isClosingReasonsLoaded && isNewClosingReason}>
              {exposures.reduce(
                (isOutstandingReserveExists, exposure) =>
                  isOutstandingReserveExists || exposure.indemnity.reserve !== 0 || exposure.expenses.reserve !== 0,
                false
              ) && (
                <div className={classes.alertBanner}>
                  <AlertBanner
                    title="Please Note"
                    note="Warning: Outstanding reserves will be zeroed."
                    alertType={AlertBanner.ALERT_TYPES.WARNING}
                    withIcon
                  />
                </div>
              )}
              {/* TODO - don't close in case of */}
              <Grid container spacing={4}>
                <Grid item xs={6}>
                  <TextFieldFormik id="reason" select label="Reason" className={classes.textField} fullWidth>
                    {possibleReasons
                      .sort((a, b) => exposuresClosingReasons[a].desc.localeCompare(exposuresClosingReasons[b].desc))
                      .map((reason) => (
                        <MenuItem key={reason} value={reason}>
                          {exposuresClosingReasons[reason].desc}
                        </MenuItem>
                      ))}
                  </TextFieldFormik>
                </Grid>
                <Grid item xs={6}>
                  <TextFieldFormik
                    id="extra"
                    label={isNewClosingReason ? 'Note' : 'Extra'}
                    disabled={!showExtra}
                    fullWidth
                    className={classes.textField}
                  />
                </Grid>
                {isNewClosingReason ? (
                  <Grid item xs={12}>
                    <CheckboxFormik
                      id="should_dismiss_all_exposure_notifications"
                      label={
                        <div className="inline-flex items-baseline">
                          Dismiss all exposure notifications
                          <TooltipIcon
                            placement="right"
                            arrow
                            title="Only notifications you are authorized to dismiss will be affected"
                          >
                            <InfoIcon className="ml-4" size={18} />
                          </TooltipIcon>
                        </div>
                      }
                    />
                  </Grid>
                ) : null}
              </Grid>
              <div className={classes.buttonsContainer}>
                <Button disabled={isSubmitting} variant="contained" color="primary" onClick={handleSubmit}>
                  Close Exposure
                </Button>
              </div>
            </LoadingSwitch>
          </CardDialog>
        );
      }}
    </Formik>
  );
}

ClosingReasonDialog.propTypes = {
  title: PropTypes.string,
  claim: PropTypes.object.isRequired,
  exposures: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onReasonChosen: PropTypes.func.isRequired,
};

export default CloseExposureDialog;
export { ClosingReasonDialog };
